import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import {
    AvatarWithBadge,
    Button,
    Flex,
    ProfileBlub,
    Sensor,
    StreemGlyphLegal,
    StreemWordmark,
    styled,
} from '@streem/ui-react';
import { recordElementClicked } from '@streem/analytics';
import { APITypes, StreemApiUserAvailabilityAvailabilityStatus } from '@streem/api';
import { observer } from 'mobx-react';
import { useGlobalStore } from '../../hooks/use_global_context';
import { AvailabilityIndicator } from '../expert_availability/availability_indicator';
import { UserFormStrategy } from '../../types/project.types';
import UserFormModal from '../../forms/user_form';
import { DeviceSettingsModal } from '../media_settings/device_settings_modal';
import { useGetCompanyStore } from '../../hooks/detail_store_hooks';
import { useActiveCompanyCode } from '../../hooks/use_active_company_code';
import { centralLoginUrl, removeProtocol } from '../../util/routing';
import { SystemNotifications } from '../allow_notifications/system_notifications';
import { autorun } from 'mobx';
import { useExpertAvailability } from '../expert_availability/hooks/use_expert_availability';
import { PageLoadModal } from '../expert_availability/modals/page_load_modal';
import { SetReadyModal } from '../expert_availability/modals/set_ready_modal';
import { useExpertAvailabilityModals } from '../expert_availability/hooks/use_expert_availability_modals';
import { useGroupReservation } from '../expert_availability/hooks/use_group_reservation';
import { StatusOptionValueType } from '../expert_availability/status_option_value';
import { LostConnectionModal } from '../expert_availability/modals/lost_connection_modal';
import { VideoDropdownSelector } from '../media_devices/video_dropdown_selector';
import { AudioDropdownSelector } from '../media_devices/audio_dropdown_selector';
import { usePromptBeforeUnload } from '../../hooks/use_prompt_before_unload';

// define default values outside render cycle
const mediaSettingsOpenedId = 'media-settings-opened';
const profileBlubOpenedId = 'profile-blub-opened';
const refreshButtonId = 'lost-connection-refresh-button';

export const TopNavBar: FC = observer(() => {
    const { userStore, authStore, companySettingsStore, sdkStore } = useGlobalStore();
    const companyCode = useActiveCompanyCode();
    const companyStore = useGetCompanyStore();
    const history = useHistory();
    const avatarButtonRef = useRef<HTMLDivElement>(null);
    const user: APITypes.StreemApiUser = userStore?.user;

    const [showProfileBlub, setShowProfileBlub] = useState(false);
    // Modal States
    const [showEditProfileModal, setShowEditProfileModal] = useState(false);
    const [showMediaSettingsModal, setShowMediaSettingsModal] = useState(false);

    const [showSystemNotifications, setShowSystemNotifications] = useState(false);

    /**
     * Expert Availability
     */
    const [
        { isOffline: expertIsOffline, isSwitchingWorkspaces, ...availabilityIndicatorProps },
        { updateUserAvailability, resetUserAvailabilityToOffline, onReloadHandler },
        setSelectedStatus,
    ] = useExpertAvailability();
    const { currentAvailabilityStatus, selectedAvailabilityStatus } = availabilityIndicatorProps;
    const {
        pageLoadModalOpen,
        activeModalState,
        closeSetReadyModal,
        closePageLoadModal,
        setActive,
        setNotReady,
    } = useExpertAvailabilityModals(
        authStore.userId,
        authStore.role,
        currentAvailabilityStatus,
        updateUserAvailability,
    );

    /**
     * Group Reservations
     */
    const { isReserved, isInCall } = useGroupReservation(
        companyCode,
        currentAvailabilityStatus,
        setSelectedStatus,
    );

    /**
     *  Group Reservations: If a user is reserved or in a call
     *  we will prompt them before navigating away from the view
     */
    usePromptBeforeUnload(isReserved || isInCall);

    /**
     * Fetch companyStore for companySettings
     */
    useEffect(() => {
        autorun(() => {
            if (!authStore.userId) return;
            const flowResult = companyStore.fetch(companyCode);
            return () => flowResult.cancel();
        });
    }, [companyCode, companyStore, authStore.userId]);

    /**
     * Component functions
     */
    const closeBlub = () => setShowProfileBlub(false);

    const handleBlubClick = useCallback(() => {
        // only record the click if showProfileBlub is false, that means the user is opening the blub.
        if (!showProfileBlub) {
            recordElementClicked(profileBlubOpenedId);
        }
        setShowProfileBlub(!showProfileBlub);
    }, [showProfileBlub]);

    const handleCloseNotificationModal = () => {
        setShowSystemNotifications(false);
    };

    const onOpenDeviceSettings = () => {
        recordElementClicked(mediaSettingsOpenedId);
        setShowMediaSettingsModal(true);
    };

    const onLogOutHandler = async () => {
        await authStore.logout();
    };

    const revealEditProfileModal = () => setShowEditProfileModal(true);
    const hideEditProfileModal = () => setShowEditProfileModal(false);

    const onAvailabilitySelectionCallback = (
        selectedOption: StatusOptionValueType,
    ): Promise<StreemApiUserAvailabilityAvailabilityStatus> =>
        updateUserAvailability(selectedOption.value.key);

    const onReloadAfterLostConnection = async () => {
        recordElementClicked(refreshButtonId);
        await onReloadHandler();
    };
    /**
     * Company Setting Conditionals
     */

    const expertStatus = companySettingsStore.expertAvailabilityEnabled
        ? selectedAvailabilityStatus.value
        : undefined;

    const workspace = companySettingsStore.switchWorkspacesEnabled
        ? {
              name: companyStore.result?.name || 'Loading…',
              imageUrl: companySettingsStore.companyLogoUrl,
              url: removeProtocol(window.location.origin),
          }
        : undefined;

    const canSwitchWorkspace =
        companySettingsStore.switchWorkspacesEnabled && authStore.associatedCompanyCount > 1;

    const hasExpertAvailabilityEnabled = companySettingsStore.expertAvailabilityEnabled;
    const showAvailabilityIndicator =
        hasExpertAvailabilityEnabled && user && sdkStore.userWallSyncConnected.current;
    const lostConnectionModalEnabled = companySettingsStore.lostConnectionModalEnabled;
    const advancedMediaSettingsEnabled = companySettingsStore.advancedMediaSettingsEnabled;

    /**
     * Current States used in components
     */
    const userWallSyncReconnectFailed = sdkStore?.userWallSyncReconnectFailed?.current;
    const isUserContextLost = !user && userStore.initialized;

    return (
        <TopNavWrapper data-testid="top-nav">
            <LogoButton data-testid="nav-streem-logo" onClick={() => history.push('/')}>
                <Flex alignItems="center">
                    <StreemGlyphLegal color="white" style={{ minWidth: '37px' }} />
                    <StreemWordmarkWrapper>
                        <StreemWordmark color="white" />
                    </StreemWordmarkWrapper>
                </Flex>
            </LogoButton>
            <Flex>
                {advancedMediaSettingsEnabled ? (
                    <>
                        <AudioDropdownSelector
                            onOpenDeviceSettings={() => setShowMediaSettingsModal(true)}
                        />
                        <VideoDropdownSelector
                            onOpenDeviceSettings={() => setShowMediaSettingsModal(true)}
                        />
                    </>
                ) : null}
                {showAvailabilityIndicator ? (
                    <AvailabilityIndicator
                        {...availabilityIndicatorProps}
                        setSelectedStatus={setSelectedStatus}
                        onSelectionCallback={onAvailabilitySelectionCallback}
                        isReserved={isReserved}
                        isInCall={isInCall}
                    />
                ) : null}
                {user ? (
                    <div ref={avatarButtonRef}>
                        <Button
                            variant="inline"
                            data-testid="nav-user-avatar"
                            data-clarity-mask="true"
                            onClick={handleBlubClick}
                        >
                            <AvatarWithBadge
                                src={user?.avatarUrl}
                                displayName={user?.name}
                                size="small"
                                statusColor={expertStatus?.color}
                                data-clarity-mask="true"
                            />
                        </Button>
                    </div>
                ) : (
                    <LoginButton />
                )}
            </Flex>
            {showProfileBlub && (
                <ProfileWrapper>
                    <Sensor
                        onClickOutside={event => {
                            if (
                                event.target instanceof Element &&
                                avatarButtonRef.current.contains(event.target)
                            )
                                return;

                            closeBlub();
                        }}
                        onEscape={closeBlub}
                    >
                        <ProfileBlub
                            email={user?.email}
                            name={user?.name}
                            statusColor={expertStatus?.color}
                            statusText={expertStatus?.label}
                            avatarUrl={user?.avatarUrl}
                            onLogOut={onLogOutHandler}
                            onEditProfile={revealEditProfileModal}
                            canSwitchWorkspace={canSwitchWorkspace}
                            centralLoginUrl={centralLoginUrl()}
                            workspace={workspace}
                            onSwitchWorkspaces={resetUserAvailabilityToOffline}
                            onOpenDeviceSettings={onOpenDeviceSettings}
                            closeBlub={closeBlub}
                            onOpenSystemNotifications={() => setShowSystemNotifications(true)}
                        />
                    </Sensor>
                </ProfileWrapper>
            )}
            {/* Modals */}
            <UserFormModal
                isOpen={showEditProfileModal}
                strategy={UserFormStrategy.EDIT_SELF}
                onCancel={hideEditProfileModal}
                onSuccess={hideEditProfileModal}
                user={user}
            />

            <DeviceSettingsModal
                isOpen={showMediaSettingsModal}
                onClose={() => setShowMediaSettingsModal(false)}
            />

            {hasExpertAvailabilityEnabled ? (
                <>
                    <SetReadyModal
                        message={activeModalState.modalMessage}
                        setActive={setActive}
                        isOpen={activeModalState.showModal}
                        closeModal={closeSetReadyModal}
                    />

                    <PageLoadModal
                        isOpen={pageLoadModalOpen}
                        closeModal={closePageLoadModal}
                        setNotReady={setNotReady}
                    />
                </>
            ) : null}

            {/* Notifications */}
            <SystemNotifications
                isOpen={showSystemNotifications}
                onClose={handleCloseNotificationModal}
            />
            {lostConnectionModalEnabled &&
                hasExpertAvailabilityEnabled &&
                !isSwitchingWorkspaces && (
                    <LostConnectionModal
                        expertIsOffline={expertIsOffline}
                        lostUser={isUserContextLost}
                        userWallSyncReconnectFailed={userWallSyncReconnectFailed}
                        onReload={onReloadAfterLostConnection}
                    />
                )}
        </TopNavWrapper>
    );
});

TopNavBar.displayName = 'TopNavBar';

const TopNavWrapper = styled(Flex)(({ theme }) => ({
    backgroundColor: theme.colors.blue90,
    alignItems: 'center',
    width: '100vw',
    height: '72px',
    padding: '16px 24px',
    position: 'sticky',
    justifyContent: 'space-between',
    zIndex: 6,
}));

const LogoButton = styled.button(({ theme }) => ({
    padding: '0',
    backgroundColor: theme.colors.blue90,
    border: 'none',
    cursor: 'pointer',
}));

const StreemWordmarkWrapper = styled.div({
    width: '100%',
});

const ProfileWrapper = styled.div(({ theme }) => ({
    position: 'absolute',
    top: theme.space[6],
    right: theme.space[5],
}));

const LoginButton: FC = () => {
    const detailsMatch = useRouteMatch<{ roomSid: string }>('/share/:token/details/:roomSid');
    const artifactMatch = useRouteMatch<{ roomSid: string; artifactSid: string }>(
        '/share/:token/details/:roomSid/artifact/:artifactSid',
    );

    const handleClick = useCallback(() => {
        const redirectUrlParts = [''];

        if (detailsMatch) {
            redirectUrlParts.push('teamcalls', detailsMatch.params.roomSid);

            if (artifactMatch) {
                redirectUrlParts.push('artifact', artifactMatch.params.artifactSid);
            }
        }

        (window as any).location.href = redirectUrlParts.join('/');
    }, [detailsMatch, artifactMatch]);

    return (
        <Button data-testid={'nav-streem-login-shared-page'} onClick={handleClick}>
            Log In
        </Button>
    );
};
